import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { APPEARANCE_PRESETS, DEFAULT_APPEARANCE, useAppearance, type Appearance } from '@/stores/appearance';
import { useNotificationPreferences } from '@/stores/notificationPreferences';
import { useSession } from '@/stores/session';
import { Icon } from '@/components/Icon';
import { useFormFactor } from '@/lib/formFactor';
import { useApi } from './kit/hooks';
import { saveSystemSettings } from './api';
import type { Shop, SystemSettings } from './types';
import { PushSetting } from './PushSetting';
import s from './SettingsHome.module.css';

const READONLY = import.meta.env.VITE_READONLY !== '0';
type Security = NonNullable<SystemSettings['security']>;
type Snapshot = { appName: string; shopName: string; security: Security };
const clone = (value: Snapshot): Snapshot => ({ appName: value.appName, shopName: value.shopName, security: { ...value.security, ipAllowlist: [...value.security.ipAllowlist] } });

export function SettingsEditor({ initial, onSaved, onHeaderActionChange }: { initial: SystemSettings; onSaved: () => void; onHeaderActionChange?: (action: ReactNode | null) => void }) {
  const formFactor = useFormFactor();
  const can = useSession((state) => state.can);
  const user = useSession((state) => state.user);
  const shops = useApi<Shop[]>(can('system.shops.write') ? '/api/shops' : null);
  const base = useMemo<Snapshot>(() => ({ appName: initial.appName, shopName: initial.shopName, security: initial.security || { passwordMinLength: 8, loginFailLimit: 20, loginLockMinutes: 10, ipAllowlist: [] } }), [initial]);
  const [draft, setDraft] = useState<Snapshot>(() => clone(base));
  const [ipInput, setIpInput] = useState(() => base.security.ipAllowlist.join('\n'));
  const [confirm, setConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState('');
  const appearance = useAppearance((state) => state.value);
  const appearanceLoaded = useAppearance((state) => state.loaded);
  const notifications = useNotificationPreferences((state) => state.value);
  const notificationsLoaded = useNotificationPreferences((state) => state.loaded);
  const [personalStatus, setPersonalStatus] = useState('');

  useEffect(() => { setDraft(clone(base)); setIpInput(base.security.ipAllowlist.join('\n')); setConfirm(false); }, [base]);
  useEffect(() => { if (user?.id) {
    if (useAppearance.getState().owner !== user.id) void useAppearance.getState().load(user.id);
    if (useNotificationPreferences.getState().owner !== user.id) void useNotificationPreferences.getState().load(user.id);
  } }, [user?.id]);

  const appChanged = draft.appName.trim() !== base.appName;
  const shopChanged = draft.shopName !== base.shopName;
  const securityChanged = JSON.stringify(draft.security) !== JSON.stringify(base.security);
  const dirty = appChanged || shopChanged || securityChanged;
  const mayEditApp = !READONLY && can('system.settings.write');
  const mayEditShop = !READONLY && can('system.shops.write');
  const mayEditSecurity = !READONLY && can('system.security.write');
  const setSecurity = (patch: Partial<Security>) => setDraft((current) => ({ ...current, security: { ...current.security, ...patch } }));
  const discard = () => { setDraft(clone(base)); setIpInput(base.security.ipAllowlist.join('\n')); setError(''); setSaved(''); setConfirm(false); };

  const submit = async () => {
    if (READONLY || saving) return;
    setSaving(true); setError(''); setSaved('');
    try {
      await saveSystemSettings({ expected: base, next: { ...draft, appName: draft.appName.trim() } });
      setConfirm(false); setSaved('已保存，V1 与 V2 使用同一份设置'); onSaved();
    } catch (cause) { setConfirm(false); setError((cause as Error).message); }
    finally { setSaving(false); }
  };
  const save = () => {
    if (!dirty) return;
    setError('');
    if (securityChanged) setConfirm(true);
    else void submit();
  };
  useEffect(() => {
    if (!onHeaderActionChange) return;
    onHeaderActionChange(<button type="button" className={s.headerSave} onClick={save} disabled={!dirty || saving || READONLY}>保存</button>);
    return () => onHeaderActionChange(null);
  }, [onHeaderActionChange, dirty, saving, draft]);
  const saveAppearance = async (value: Appearance) => {
    if (!user?.id) return;
    useAppearance.getState().setValue(value);
    const result = await useAppearance.getState().save(user.id, value);
    setPersonalStatus(result === 'synced' ? '外观已同步到账号' : '外观已保存在本机');
  };
  const toggleNotification = async (key: 'sound' | 'speech') => {
    if (!user?.id) return;
    const next = { ...notifications, [key]: !notifications[key] };
    const result = await useNotificationPreferences.getState().save(user.id, next);
    setPersonalStatus(result === 'synced' ? '通知偏好已同步到账号' : '通知偏好已保存在本机');
  };
  const activePreset = APPEARANCE_PRESETS.find(({ value }) => JSON.stringify(value) === JSON.stringify(appearance))?.key;
  const securityDiffs = [
    { label: '密码最小长度', before: String(base.security.passwordMinLength), after: String(draft.security.passwordMinLength) },
    { label: '登录失败上限', before: String(base.security.loginFailLimit), after: String(draft.security.loginFailLimit) },
    { label: '锁定时长', before: String(base.security.loginLockMinutes), after: String(draft.security.loginLockMinutes) },
    {
      label: 'IP 白名单',
      before: base.security.ipAllowlist.length ? base.security.ipAllowlist.join('、') : '不限制',
      after: draft.security.ipAllowlist.length ? draft.security.ipAllowlist.join('、') : '不限制',
      compactBefore: base.security.ipAllowlist.length ? `${base.security.ipAllowlist.length} 条` : '不限制',
      compactAfter: draft.security.ipAllowlist.length ? `${draft.security.ipAllowlist.length} 条` : '不限制',
    },
  ].filter((item) => item.before !== item.after);
  const phoneOrTablet = formFactor !== 'desktop';
  const ipAllowlistChanged = JSON.stringify(base.security.ipAllowlist) !== JSON.stringify(draft.security.ipAllowlist);

  return <div className={s.editor}>
    {!onHeaderActionChange && <div className={s.actions}>
      <span className={s.actionHint}>{READONLY ? '当前为只读预览；系统设置写入未开启' : dirty ? '有未保存的系统设置' : '系统设置已是最新'}</span>
      <button type="button" onClick={discard} disabled={!dirty || saving}>放弃修改</button>
      <button type="button" className={s.primary} onClick={save} disabled={!dirty || saving || READONLY}>保存</button>
    </div>}
    {error && <p role="alert" className={s.error}>{error}</p>}
    {saved && <p role="status" className={s.saved}>{saved}</p>}
    <section className={s.card}>
      <h2>系统名称</h2>
      <div className={s.fields}>
        <label>系统名称<input value={draft.appName} maxLength={100} disabled={!mayEditApp} onChange={(event) => setDraft((current) => ({ ...current, appName: event.target.value }))} /><small>显示在登录页、侧边栏和浏览器标签；保存后在线用户可看到新名称。</small></label>
        <label>默认店铺<select value={draft.shopName} disabled={!mayEditShop || shops.loading || !!shops.error} onChange={(event) => setDraft((current) => ({ ...current, shopName: event.target.value }))}>
          <option value="">未设置</option>
          {draft.shopName && !shops.data?.some((shop) => shop.name === draft.shopName) && <option value={draft.shopName}>{draft.shopName}</option>}
          {shops.data?.map((shop) => <option key={shop.id} value={shop.name}>{shop.name}</option>)}
        </select><small>新建订单时默认带出。{shops.error ? '店铺列表加载失败，请刷新后重试。' : ''}</small></label>
      </div>
    </section>
    <section className={s.card}>
      <h2>账号安全策略 <small>下限由系统强制</small></h2>
      <div className={s.securityFields}>
        {([
          ['passwordMinLength', '密码最小长度', 8, 128, '下限 8 位'],
          ['loginFailLimit', '登录失败上限', 3, 100, '下限 3 次'],
          ['loginLockMinutes', '锁定时长（分钟）', 1, 1440, '下限 1 分钟'],
        ] as const).map(([key, label, min, max, hint]) => <label key={key}>{label}<input type="number" min={min} max={max} value={draft.security[key]} disabled={!mayEditSecurity} onChange={(event) => setSecurity({ [key]: Number(event.target.value) })} /><small>{hint}</small></label>)}
      </div>
      <label className={s.ipLabel}>IP 白名单<textarea value={ipInput} disabled={!mayEditSecurity} rows={3} placeholder="留空表示不限制；每行填写一个完整 IP" onChange={(event) => { setIpInput(event.target.value); setSecurity({ ipAllowlist: event.target.value.split(/[\n,]/).map((ip) => ip.trim()).filter(Boolean) }); }} /><small>只支持完整 IPv4/IPv6 地址。保存时校验当前 IP；无法确认时拒绝保存。</small></label>
      <p className={s.alert}>IP 白名单填错可能阻止后续登录。代理环境需先正确配置 TRUST_PROXY_HEADERS。</p>
    </section>
    <section className={s.card}>
      <h2>外观与密度 <small>只对当前账号生效</small></h2>
      <div className={s.presets}>{APPEARANCE_PRESETS.map((preset) => <button type="button" key={preset.key} className={activePreset === preset.key ? s.selected : ''} aria-pressed={activePreset === preset.key} disabled={!appearanceLoaded} onClick={() => void saveAppearance(preset.value)}>{preset.label}</button>)}<button type="button" disabled={!appearanceLoaded} onClick={() => void saveAppearance(DEFAULT_APPEARANCE)}>恢复默认</button></div>
      <div className={s.ranges}>{([
        ['fontSize', '字号', 12, 18], ['lineHeight', '行高', 18, 30], ['gap', '间距', 6, 18],
      ] as const).map(([key, label, min, max]) => <label key={key}>{label}<input type="range" min={min} max={max} value={appearance[key]} disabled={!appearanceLoaded} onChange={(event) => useAppearance.getState().setValue({ ...useAppearance.getState().value, [key]: Number(event.target.value) })} onPointerUp={() => void saveAppearance(useAppearance.getState().value)} onKeyUp={() => void saveAppearance(useAppearance.getState().value)} onBlur={() => void saveAppearance(useAppearance.getState().value)} /><span>{appearance[key]} px</span></label>)}</div>
      <div className={s.preview} style={{ fontSize: appearance.fontSize, lineHeight: `${appearance.lineHeight}px`, padding: appearance.gap + 4 }}><b>订单号 · 客户 · 金额</b><div>XS20260921003　南通盛辉家居　￥72,400.00</div><div>XS20260918007　宁波辉盛日用品　￥38,600.00</div></div>
    </section>
    <section className={s.card}>
      <h2>消息推送 <small>当前账号偏好</small></h2>
      <div className={s.toggleGrid}>
        <div><b>站内实时提醒</b><small>浏览器打开时接收</small><span>已开启</span></div>
        <PushSetting />
        <div><b>提示音</b><small>可单独关闭</small><input aria-label="提示音" type="checkbox" checked={notifications.sound} disabled={!notificationsLoaded} onChange={() => void toggleNotification('sound')} /></div>
        <div><b>语音播报</b><small>跟随媒体音量</small><input aria-label="语音播报" type="checkbox" checked={notifications.speech} disabled={!notificationsLoaded || !notifications.sound} onChange={() => void toggleNotification('speech')} /></div>
      </div>
      {personalStatus && <p role="status" className={s.saved}>{personalStatus}</p>}
    </section>
    {confirm && <div className={s.overlay} role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setConfirm(false); }}><div role="dialog" aria-modal="true" aria-label={phoneOrTablet ? '保存账号安全策略？' : '确认保存账号安全策略'} className={`${s.dialog} ${formFactor === 'phone' ? s.phoneDialog : formFactor === 'tablet' ? s.tabletDialog : ''}`}>
      <div className={phoneOrTablet ? s.dialogBodyCompact : undefined}>
        {phoneOrTablet && <div className={s.dialogIcon}><Icon name="shield" size={22} /></div>}
        <h2>{phoneOrTablet ? '保存账号安全策略？' : '确认保存安全策略'}</h2>
        <p>{phoneOrTablet ? <>这 {securityDiffs.length} 项改动对<b>所有账号</b>生效：</> : '以下更改会影响所有账号的后续登录：'}</p>
        {phoneOrTablet ? <div className={s.diffList}>{securityDiffs.map((item) => <div key={item.label}><span>{item.label}</span><b><del>{item.compactBefore ?? item.before}</del><i aria-hidden="true">→</i>{item.compactAfter ?? item.after}</b></div>)}</div> : <dl>{securityDiffs.map((item) => <div key={item.label}><dt>{item.label}</dt><dd>{item.before} → {item.after}</dd></div>)}</dl>}
        {phoneOrTablet ? <div className={s.dialogNotice}><p>
          {ipAllowlistChanged && draft.security.ipAllowlist.length > 0 && <><b>保存白名单变更时，服务端会验证当前 IP。</b>无法确认时会拒绝保存。 </>}
          已登录账号不会被踢；白名单下次登录校验，新密码要求下次改密生效。
        </p></div> : <p className={s.alert}>如当前 IP 不在新白名单内，服务端将拒绝保存。</p>}
      </div>
      <div className={`${s.dialogActions} ${phoneOrTablet ? s.dialogActionsCompact : ''}`}><button type="button" onClick={() => setConfirm(false)} disabled={saving}>{phoneOrTablet ? '暂不保存' : '返回修改'}</button><button type="button" className={s.primary} onClick={() => void submit()} disabled={saving}>{saving ? '保存中…' : phoneOrTablet ? '保存并生效' : '确认保存'}</button></div>
    </div></div>}
  </div>;
}
